<script setup>
// Hesaba yuklenmis gorseller ve videolar.
//
// Gorseller tablo yerine izgara olarak gosteriliyor: bir gorseli hash'inden
// tanimak mumkun degil, kullanicinin kreatife hangi hash'i yazacagini
// secebilmesi icin onizleme sart. Videolarda oynatma URL'i ayri bir arac
// cagrisi gerektirdiginden onlar tabloda kaliyor.
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { aracCalistir, diziyeCevir } from './kopru.js';
import AracTablosu from './AracTablosu.vue';

const props = defineProps({
  hesap: { type: String, default: '' },
});

const { t } = useI18n();
const route = useRoute();

const gorunum = ref('gorseller');
const gorseller = ref([]);
const videolar = ref([]);
const yukleniyor = ref(false);
const hata = ref('');

const panelAcik = ref(false);
const panelBaslik = ref('');
const panelVeri = ref(null);
const panelYukleniyor = ref(false);

const yuklemeAcik = ref(false);
const yuklemeYolu = ref('');
const yuklemeAdi = ref('');
const yukleniyorMedya = ref(false);

const tarihBicimle = deger => {
  if (!deger) return '—';
  const tarih = new Date(deger);
  return Number.isNaN(tarih.getTime())
    ? String(deger)
    : tarih.toLocaleString('tr-TR');
};

// Meta video suresini saniye (ondalikli) olarak dondurur; ham "12.3666" hicbir
// sey anlatmiyor, tam saniyeye yuvarlayip birim ekliyoruz.
const sureBicimle = deger => {
  const sayi = Number(deger);
  if (!deger && deger !== 0) return '—';
  if (Number.isNaN(sayi)) return String(deger);
  return t('META_ADS.MEDYA.PANEL.SANIYE', { sayi: Math.round(sayi) });
};

// Video durumu bazen duz metin, bazen {video_status: 'ready'} nesnesi gelir;
// nesne gelirse tabloda "[object Object]" yazardi.
const durumBicimle = deger => {
  if (deger && typeof deger === 'object') {
    return String(deger.video_status || deger.status || '—');
  }
  return deger ? String(deger) : '—';
};

const videoSutunlari = computed(() => [
  { anahtar: 'id', baslik: t('META_ADS.MEDYA.SUTUN.ID') },
  { anahtar: 'title', baslik: t('META_ADS.MEDYA.SUTUN.BASLIK') },
  { anahtar: 'status', baslik: t('META_ADS.MEDYA.SUTUN.DURUM'), bicim: durumBicimle },
  { anahtar: 'created_time', baslik: t('META_ADS.MEDYA.SUTUN.TARIH'), bicim: tarihBicimle },
  { anahtar: 'length', baslik: t('META_ADS.MEDYA.SUTUN.SURE'), bicim: sureBicimle },
]);

// Ayrinti panelinde ham JSON basiliyordu. Video nesnesinin alan adlari Meta
// tarafinda sabit oldugu icin bilinenleri Turkce etikete ceviriyoruz;
// bilinmeyen alan adi oldugu gibi kaliyor ki veri kaybolmasin.
const ALAN_ETIKETLERI = {
  id: 'META_ADS.MEDYA.ALAN.ID',
  title: 'META_ADS.MEDYA.ALAN.BASLIK',
  name: 'META_ADS.MEDYA.ALAN.AD',
  description: 'META_ADS.MEDYA.ALAN.ACIKLAMA',
  status: 'META_ADS.MEDYA.ALAN.DURUM',
  length: 'META_ADS.MEDYA.ALAN.SURE',
  format: 'META_ADS.MEDYA.ALAN.BICIM',
  source: 'META_ADS.MEDYA.ALAN.KAYNAK',
  picture: 'META_ADS.MEDYA.ALAN.ONIZLEME',
  thumbnail_url: 'META_ADS.MEDYA.ALAN.ONIZLEME',
  thumbnails: 'META_ADS.MEDYA.ALAN.ONIZLEMELER',
  permalink_url: 'META_ADS.MEDYA.ALAN.YAYIN_BAGLANTISI',
  embed_html: 'META_ADS.MEDYA.ALAN.GOMME_KODU',
  published: 'META_ADS.MEDYA.ALAN.YAYINDA',
  account_id: 'META_ADS.MEDYA.ALAN.HESAP_ID',
  hash: 'META_ADS.MEDYA.ALAN.GORSEL_HASH',
  width: 'META_ADS.MEDYA.ALAN.GENISLIK',
  height: 'META_ADS.MEDYA.ALAN.YUKSEKLIK',
  created_time: 'META_ADS.MEDYA.ALAN.OLUSTURMA',
  updated_time: 'META_ADS.MEDYA.ALAN.GUNCELLEME',
};

const TARIH_ALANLARI = ['created_time', 'updated_time'];

const etiketle = anahtar =>
  ALAN_ETIKETLERI[anahtar] ? t(ALAN_ETIKETLERI[anahtar]) : anahtar;

// Ic ice nesne/dizi tek satira sigmaz; sayisini soyleyip paneli okunur
// tutuyoruz. Bos alan hic basilmiyor: dolu sanilmasin.
const degerBicimle = (anahtar, deger) => {
  if (deger === null || deger === undefined || deger === '') return '';
  if (anahtar === 'status') return durumBicimle(deger);
  if (anahtar === 'length') return sureBicimle(deger);
  if (typeof deger === 'boolean') {
    return deger
      ? t('META_ADS.MEDYA.PANEL.EVET')
      : t('META_ADS.MEDYA.PANEL.HAYIR');
  }
  if (Array.isArray(deger)) {
    return deger.length
      ? t('META_ADS.MEDYA.PANEL.OGE_SAYISI', { sayi: deger.length })
      : '';
  }
  if (typeof deger === 'object') {
    const sayi = Object.keys(deger).length;
    return sayi ? t('META_ADS.MEDYA.PANEL.ALAN_SAYISI', { sayi }) : '';
  }
  if (TARIH_ALANLARI.includes(anahtar)) return tarihBicimle(deger);
  return String(deger);
};

// Arac kimi zaman tek nesne kimi zaman dizi dondurur; ikisi de ayni kaliba
// giriyor ki sablonda iki ayri dal olmasin.
const panelKayitlari = computed(() => {
  const v = panelVeri.value;
  if (!v || typeof v !== 'object') return [];
  return (Array.isArray(v) ? v : [v])
    .filter(kayit => kayit && typeof kayit === 'object')
    .map(kayit =>
      Object.entries(kayit)
        .map(([anahtar, deger]) => ({
          anahtar,
          etiket: etiketle(anahtar),
          deger: degerBicimle(anahtar, deger),
        }))
        .filter(alan => alan.deger !== '')
    )
    .filter(kayit => kayit.length);
});

// Arac yalnizca metin ozeti urettiyse onu oldugu gibi gosteriyoruz.
const panelMetni = computed(() =>
  typeof panelVeri.value === 'string' ? panelVeri.value : ''
);

// Video onizlemesi varsa once o gosteriliyor: bir videoyu kimliginden
// tanimak mumkun degil.
const panelGorsel = computed(() => {
  const v = panelVeri.value;
  if (!v || typeof v !== 'object' || Array.isArray(v)) return '';
  return v.picture || v.thumbnail_url || '';
});

const panelBos = computed(
  () => !panelMetni.value && !panelKayitlari.value.length
);

// Gorsel nesnesinde onizleme adresi araca gore url, permalink_url ya da
// thumbnail olabiliyor; ilk dolu olani kullaniliyor.
const gorselAdresi = g => g?.url || g?.permalink_url || g?.url_128 || g?.thumbnail_url || '';

const yukle = async () => {
  if (!props.hesap) return;
  yukleniyor.value = true;
  hata.value = '';
  try {
    const arac =
      gorunum.value === 'gorseller' ? 'ads_get_ad_images' : 'ads_get_ad_videos';
    const sonuc = await aracCalistir(route.params.accountId, arac, {
      account_id: props.hesap,
    });
    const liste = diziyeCevir(sonuc);
    if (gorunum.value === 'gorseller') gorseller.value = liste;
    else videolar.value = liste;
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
    if (gorunum.value === 'gorseller') gorseller.value = [];
    else videolar.value = [];
  } finally {
    yukleniyor.value = false;
  }
};

const gorunumDegistir = ad => {
  gorunum.value = ad;
  yuklemeAcik.value = false;
  yukle();
};

const videoPanel = async (satir, arac, baslik) => {
  panelAcik.value = true;
  panelBaslik.value = baslik;
  panelVeri.value = null;
  panelYukleniyor.value = true;
  hata.value = '';
  try {
    const sonuc = await aracCalistir(route.params.accountId, arac, {
      video_id: satir.id,
    });
    panelVeri.value = sonuc.veri ?? sonuc.ozet ?? null;
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
    panelAcik.value = false;
  } finally {
    panelYukleniyor.value = false;
  }
};

const videoAyrinti = satir =>
  videoPanel(satir, 'ads_get_video_details', t('META_ADS.MEDYA.VIDEO_AYRINTI'));

const videoMedya = satir =>
  videoPanel(satir, 'ads_get_video_media', t('META_ADS.MEDYA.VIDEO_MEDYA'));

const medyaYukle = async () => {
  if (!props.hesap || !yuklemeYolu.value.trim()) return;
  if (!window.confirm(t('META_ADS.MEDYA.YUKLE_ONAY'))) return;
  yukleniyorMedya.value = true;
  hata.value = '';
  try {
    const gorselMi = gorunum.value === 'gorseller';
    // Yukleme MCP sunucusunun uzerinde calisiyor; tarayicidan dosya
    // gonderilemez, bu yuzden sunucunun erisebildigi bir yol isteniyor.
    const argumanlar = { account_id: props.hesap };
    if (gorselMi) argumanlar.image_path = yuklemeYolu.value.trim();
    else argumanlar.video_path = yuklemeYolu.value.trim();
    if (yuklemeAdi.value.trim()) argumanlar.name = yuklemeAdi.value.trim();

    await aracCalistir(
      route.params.accountId,
      gorselMi ? 'ads_upload_ad_image' : 'ads_upload_ad_video',
      argumanlar,
      { onaylandi: true }
    );
    useAlert(t('META_ADS.MEDYA.YUKLENDI'));
    yuklemeYolu.value = '';
    yuklemeAdi.value = '';
    yuklemeAcik.value = false;
    await yukle();
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    yukleniyorMedya.value = false;
  }
};

watch(() => props.hesap, yukle);
onMounted(yukle);
</script>

<template>
  <div class="p-6">
    <p v-if="!hesap" class="text-sm text-n-slate-11">
      {{ t('META_ADS.ORTAK.HESAP_SECIN') }}
    </p>

    <template v-else>
      <div class="flex flex-wrap items-center gap-2 mb-4">
        <Button
          size="sm"
          :variant="gorunum === 'gorseller' ? 'solid' : 'faded'"
          icon="i-lucide-image"
          :label="t('META_ADS.MEDYA.GORSELLER')"
          @click="gorunumDegistir('gorseller')"
        />
        <Button
          size="sm"
          :variant="gorunum === 'videolar' ? 'solid' : 'faded'"
          icon="i-lucide-video"
          :label="t('META_ADS.MEDYA.VIDEOLAR')"
          @click="gorunumDegistir('videolar')"
        />
        <span class="grow" />
        <Button
          size="sm"
          variant="faded"
          icon="i-lucide-refresh-cw"
          :label="t('META_ADS.ORTAK.YENILE')"
          @click="yukle"
        />
        <Button
          size="sm"
          variant="solid"
          icon="i-lucide-upload"
          :label="t('META_ADS.MEDYA.YUKLE')"
          @click="yuklemeAcik = !yuklemeAcik"
        />
      </div>

      <p v-if="hata" class="mb-3 text-sm text-n-ruby-11">{{ hata }}</p>

      <form
        v-if="yuklemeAcik"
        class="grid gap-3 p-4 mb-5 border rounded-lg sm:grid-cols-2 border-n-weak"
        @submit.prevent="medyaYukle"
      >
        <label class="text-sm text-n-slate-11">
          {{ t('META_ADS.MEDYA.FORM.YOL') }}
          <!-- required: yol bosken medyaYukle sessizce donuyordu, dugme olu gorunurdu -->
          <input
            v-model="yuklemeYolu"
            type="text"
            required
            placeholder="/data/reklam/gorsel.jpg"
          />
        </label>
        <label class="text-sm text-n-slate-11">
          {{ t('META_ADS.MEDYA.FORM.AD') }}
          <input v-model="yuklemeAdi" type="text" />
        </label>
        <p class="text-xs sm:col-span-2 text-n-slate-11">
          {{ t('META_ADS.MEDYA.FORM.IPUCU') }}
        </p>
        <div class="flex gap-2 sm:col-span-2">
          <Button
            size="sm"
            variant="solid"
            type="submit"
            :is-loading="yukleniyorMedya"
            :label="t('META_ADS.MEDYA.YUKLE')"
          />
          <Button
            type="button"
            size="sm"
            variant="faded"
            :label="t('META_ADS.ORTAK.VAZGEC')"
            @click="yuklemeAcik = false"
          />
        </div>
      </form>

      <div v-if="gorunum === 'gorseller'">
        <div v-if="yukleniyor" class="flex justify-center p-10">
          <Spinner class="text-n-brand" />
        </div>
        <p v-else-if="!gorseller.length" class="p-8 text-sm text-center text-n-slate-11">
          {{ t('META_ADS.MEDYA.GORSEL_YOK') }}
        </p>
        <div v-else class="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          <figure
            v-for="(g, i) in gorseller"
            :key="g.hash || g.id || i"
            class="overflow-hidden border rounded-lg border-n-weak"
          >
            <img
              v-if="gorselAdresi(g)"
              :src="gorselAdresi(g)"
              :alt="g.name || ''"
              loading="lazy"
              class="object-cover w-full h-32 bg-n-alpha-1"
            />
            <div v-else class="flex items-center justify-center w-full h-32 bg-n-alpha-1">
              <span class="text-xs text-n-slate-11">
                {{ t('META_ADS.MEDYA.ONIZLEME_YOK') }}
              </span>
            </div>
            <figcaption class="p-2 text-xs truncate text-n-slate-12" :title="g.name">
              {{ g.name || g.id || '—' }}
            </figcaption>
            <!-- Hash kreatif olustururken elle yazildigi icin secilebilir birakildi -->
            <p v-if="g.hash" class="px-2 pb-2 text-[11px] truncate text-n-slate-11 select-all">
              {{ g.hash }}
            </p>
          </figure>
        </div>
      </div>

      <AracTablosu
        v-else
        :satirlar="videolar"
        :sutunlar="videoSutunlari"
        :yukleniyor="yukleniyor"
        :bos-metin="t('META_ADS.MEDYA.VIDEO_YOK')"
      >
        <template #eylemler="{ satir }">
          <Button
            size="sm"
            variant="faded"
            :label="t('META_ADS.ORTAK.AYRINTI')"
            @click="videoAyrinti(satir)"
          />
          <Button
            size="sm"
            variant="faded"
            class="ml-2"
            :label="t('META_ADS.MEDYA.MEDYA')"
            @click="videoMedya(satir)"
          />
        </template>
      </AracTablosu>

      <div
        v-if="panelAcik"
        class="fixed inset-0 z-50 flex items-center justify-center p-6 bg-n-alpha-black2"
        @click.self="panelAcik = false"
      >
        <div
          class="w-full max-w-2xl max-h-[80vh] overflow-auto rounded-lg bg-n-surface-1 border border-n-weak"
        >
          <div class="flex items-center gap-2 px-4 py-3 border-b border-n-weak">
            <h3 class="text-sm font-medium text-n-slate-12">{{ panelBaslik }}</h3>
            <span class="grow" />
            <Button
              size="sm"
              variant="faded"
              :label="t('META_ADS.ORTAK.KAPAT')"
              @click="panelAcik = false"
            />
          </div>
          <div class="p-4">
            <div v-if="panelYukleniyor" class="flex justify-center p-8">
              <Spinner class="text-n-brand" />
            </div>
            <template v-else>
              <img
                v-if="panelGorsel"
                :src="panelGorsel"
                alt=""
                class="mb-4 border rounded max-h-64 border-n-weak"
              />

              <p
                v-if="panelMetni"
                class="text-sm whitespace-pre-line text-n-slate-11"
              >
                {{ panelMetni }}
              </p>

              <div
                v-for="(kayit, i) in panelKayitlari"
                :key="i"
                class="py-3 border-b last:border-b-0 border-n-weak"
              >
                <dl class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
                  <template v-for="alan in kayit" :key="alan.anahtar">
                    <dt class="text-n-slate-11">{{ alan.etiket }}</dt>
                    <dd class="break-words text-n-slate-12">{{ alan.deger }}</dd>
                  </template>
                </dl>
              </div>

              <p v-if="panelBos" class="py-6 text-sm text-center text-n-slate-11">
                {{ t('META_ADS.MEDYA.PANEL.BOS') }}
              </p>
            </template>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
